import { Direction } from '@angular/cdk/bidi';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ImageCarouselComponent } from 'src/app/@shared/components/pop-ups/image-carousel/image-carousel.component';
import { Language, LanguageService } from 'src/app/@shared/services/language/language.service';
import { ModalService } from 'src/app/@shared/services/modal/modal.service';
import { QueryParamsService } from 'src/app/@shared/services/query-params/query-params.service';
import { UtilsService } from 'src/app/@shared/services/utils/utils.service';
import { eAgencyNames } from 'src/app/modules/route-planner/constants/agencies.constants';
import { BOARD_ON, BUS, CARMELIT, LIGHT_TRAIN, RAHBALIT, STATION_CODE, STATION_PICTURE, STATIONS, TRAIN, BOARD_OFF, CABLE_CAR, SPECIAL_DATA_TEXTS, RESERVE_IS_POSSIBLE, TO_DIRECTION, PLATFORM, SHOW_LINE_CALENDAR, ONE_STATION, TRAIN_NUMBER } from 'src/app/modules/route-planner/constants/multi-language.constants';
import { eTransportationOptions, eTransportationOptionsHerbrew } from 'src/app/modules/route-planner/constants/transportation-options.constants';
import { eTab } from 'src/app/modules/route-planner/enums/route-planning/tabs/tabs.enums';
import { eTransportMode } from 'src/app/modules/route-planner/enums/route-planning/transport-mode.enum';
import { Coordinate } from 'src/app/modules/route-planner/interfaces/location/coordinate.interface';
import { Agency } from 'src/app/modules/route-planner/interfaces/route-planning/agency.interface';
import { Place } from 'src/app/modules/route-planner/interfaces/route-planning/place.interface';
import { Section } from 'src/app/modules/route-planner/interfaces/route-planning/section.interface';
import { RoutePlanningService } from 'src/app/modules/route-planner/services/route-planning/route-planning.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-detailed-result-route-transit',
  templateUrl: './detailed-result-route-transit.component.html',
  styleUrls: ['./detailed-result-route-transit.component.scss'],
  standalone: false
})
export class DetailedResultRouteTransitComponent implements OnInit {
  @Input() section: Section;
  @Input() index: number;
  @Input() length: number;
  @Input() isWalkedToStop: boolean = false;
  @Output() focusLocationEvent: EventEmitter<Coordinate> = new EventEmitter<Coordinate>();
  @Input() prevSection: Section | null;

  onBoardText: string
  stationPictureText: string
  stationsText: string
  calculatedSectionDurationText: string
  stationCodeText: string
  platformText: string

  direction: Direction
  boardOffText: string;
  accessibleTooltipText: string
  accessibleWithOrderTooltipText: string
  accessibleAriaLabel: string
  accessibleWithOrderAriaLabel: string
  orderInAdvancedTooltipText: string
  toDirectionText: string
  trainNumberText: string
  showLineCalendarText: string
  oneStationText: string

  constructor(
    public readonly routePlanningService: RoutePlanningService,
    public readonly languageService: LanguageService,
    private readonly modalService: ModalService,
    private readonly utilsService: UtilsService
  ) {

  }

  ngOnInit(): void {
    this.initializeMultiLanguageTexts()
  }

  getTransportMethodName(transportMode: string, agency: Agency, language: Language) {
    switch (transportMode) {
      case eTransportationOptions.BUS:
        return BUS[language.language]
      case eTransportationOptions.TRAIN:
        return TRAIN[language.language]
      case eTransportationOptions.LIGHT_TRAIN:
        if (agency.name === eAgencyNames.CARMELIT || agency.name === eAgencyNames.RAHBALIT) {
          return CABLE_CAR[language.language]
        }
        return LIGHT_TRAIN[language.language]
      case eTransportationOptions.CARMELIT:
        return CARMELIT[language.language]
      case eTransportationOptions.RAHBALIT:
        return RAHBALIT[language.language]
      default:
        return "";
    }
  }

  initializeMultiLanguageTexts() {
    this.languageService.selectedLanguage$.subscribe((language) => {
      this.direction = language.direction;
      this.calcSectionDuration();
      const transportName = this.getTransportMethodName(
        this.section.transport.mode,
        this.section.agency,
        language
      );
      this.onBoardText = `${BOARD_ON[language.language]} ${transportName}`;
      this.boardOffText = `${BOARD_OFF[language.language]}`;
      this.stationPictureText = STATION_PICTURE[language.language];
      this.stationsText = STATIONS[language.language];
      this.stationCodeText = STATION_CODE[language.language];
      this.accessibleTooltipText = SPECIAL_DATA_TEXTS.accessibleTooltip[language.language]
      this.accessibleWithOrderTooltipText = SPECIAL_DATA_TEXTS.accessibleWithOrderTooltip[language.language]
      this.accessibleAriaLabel = SPECIAL_DATA_TEXTS.accessibleButtonAriaLabel[language.language]
      this.accessibleWithOrderAriaLabel = SPECIAL_DATA_TEXTS.accessibleWithOrderButtonAriaLabel[language.language]
      this.orderInAdvancedTooltipText = RESERVE_IS_POSSIBLE[language.language]
      this.toDirectionText = TO_DIRECTION[language.language]
      this.trainNumberText= TRAIN_NUMBER[language.language]
      this.platformText = PLATFORM[language.language]
      this.showLineCalendarText = SHOW_LINE_CALENDAR[language.language]
      this.oneStationText = ONE_STATION[language.language]
    });
  }

  calcSectionDuration() {
    this.calculatedSectionDurationText =
      this.routePlanningService.calcSectionDuration(
        this.section.travelSummary.duration
      );
  }

  openStationImages(images: string[]) {
    this.modalService.openDialog(ImageCarouselComponent, {
      data: {
        imagesUrl: images ?? [],
      },
    });
  }

  convertIntermediateStopTime(time) {
    let formatedTime: string
    if (typeof time === "string") {
      const israelTimeZone = 'Asia/Jerusalem';

      const date = new Date(time);

      const formatter = new Intl.DateTimeFormat('en-GB', {
        timeZone: israelTimeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });

      const parts = formatter.formatToParts(date);

      const hour = parts.find(p => p.type === 'hour').value;
      const minute = parts.find(p => p.type === 'minute').value;

      formatedTime = `${hour}:${minute}`;
    } else {
      const isoString = time.toISOString()
      const date = new Date(isoString);
      date.setHours(date.getHours() + 1);
      const newIsoString = date.toISOString();
      const timePart = newIsoString.split("T")[1];
      const hoursMinutes = timePart.substring(0, 5);
      formatedTime = hoursMinutes
    }

    return formatedTime
  }

  openContactInfoModal() {
    this.modalService.openAgencyContactInfoModal(parseInt(this.section.agency.id), this.section.specialData.isCallNeeded || this.section.specialData.isOrderInAdvanced ? true : false);
  }

  openStationInNewTab(stopCode: string) {
    const sanitizedStopCode = encodeURIComponent(stopCode);
    const trustedBase = environment.maslulan_url.replace(/\/+$/, '');
    const url = `${trustedBase}/?tab=stations&saveParams=true&stop=${sanitizedStopCode}`;

    window.open(url, '_blank', 'noopener,noreferrer');
  }

  handleStationClick(place: Place) {
    this.focusLocationEvent.emit(place.location)
    this.openStationInNewTab(place.code)
  }

  pickLine(routeId: number, routeName: string) {

    const trustedBase = environment.maslulan_url.replace(/\/+$/, '');
    const url = `${trustedBase}/?tab=lines&saveParams=true&routeId=${routeId}&line=${routeName}&showCalendar=true`;

    window.open(url, '_blank', 'noopener,noreferrer');
  }

  isTrainSection(section: Section): boolean {
    return section.transport.mode === eTransportMode.REGIONAL_TRAIN
  }
}
